import { useAuth } from "@clerk/clerk-react";
import { Grid } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { SearchParams } from "schemas/dashboard";
import { routes } from "schemas/routes";
import { HOME_BODY_COLOR } from "styles/oneOffColor";

import Loading from "components/Loading";
import { BRAND_PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";

import { AuthAction } from "../schema";
import BrandOnboardingSignup from "./Signup";

const styles = {
  root: {
    backgroundColor: HOME_BODY_COLOR,
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    position: "fixed",
    overflowY: "auto",
    px: { xs: 0, sm: 3, md: 5, lg: 20 },
    py: { xs: 0, sm: 2, md: 5 },
    opacity: 1,
    backgroundSize: "22px 22px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    borderRadius: { xs: 0, sm: 4 },
    backgroundColor: HOME_BODY_COLOR,
    maxWidth: { lg: "1024px" },
    p: { xs: 3, sm: 5, md: 8 },
  },
  textField: {
    backgroundColor: "white",
    "& .MuiInputBase-root": { backgroundColor: "white" },
    "& .MuiFormHelperText-root": { color: "error.main" },
  },
  helperText: {},
};

const BrandOnboardingFlow = () => {
  const { currentOrg, setCurrentUser, setCurrentOrg } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);

  const source = getSource();

  const emailMatch = location.search?.match(/email=([^&]*)/);
  const email = emailMatch ? decodeURIComponent(emailMatch[1]) : "";

  const fetchUserInfo = async () => {
    setFetchUserLoading(true);
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      if (res) {
        identifyUser(res.organizationUser.id);
        setCurrentUser(res.organizationUser);
        setCurrentOrg(res.organization);
      }
      redirectToPage();
    } catch (error) {
      handleClerkUserSignUp();
    }
  };

  const handleClerkUserSignUp = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          source: referralCode ? "referral" : source,
          isBrand: true,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
    } catch (error) {
      setErrorAlert(error);
      setFetchUserLoading(false);
    }
  };

  const redirectToPage = async () => {
    setFetchUserLoading(true);
    if (currentOrg?.id) {
      navigate(`/${routes.home}${location.search}`, {
        replace: true,
      });
    } else {
      navigate(`/brands/sign-up${location.search}`, {
        replace: true,
      });
    }
    setFetchUserLoading(false);
  };

  useEffect(() => {
    if (currentOrg?.id) {
      redirectToPage();
    } else {
      fetchUserInfo();
      trackEvent(BRAND_PAGE_VISITED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <Grid container sx={styles.root}>
      <Grid sx={styles.formContainer} item xs={12}>
        <Routes>
          <Route index element={<Navigate to="sign-up" replace />} />
          <Route
            path="sign-up"
            element={
              <BrandOnboardingSignup
                email={email}
                action={AuthAction.SIGN_UP}
              />
            }
          />
          <Route
            path="sign-in"
            element={
              <BrandOnboardingSignup
                email={email}
                action={AuthAction.SIGN_IN}
              />
            }
          />
        </Routes>
      </Grid>
    </Grid>
  );
};

export default BrandOnboardingFlow;
